import { Bell, ChevronRight, Copy, KeyRound, Palette, Rocket, ScrollText, SlidersHorizontal, TriangleAlert, Users } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Link } from 'react-router';
import { Banner, IconButton, OrgusMark, Skeleton, StatusPill, cn, formatInt, formatRelative, plural, shortSha, toast, useNow } from '@orgus/ui';
import { useConnection, useOrgus } from '../../../lib/store.ts';
import { RestartHub } from '../../lifecycle/RestartHub.tsx';
import { useMe } from '../../auth/useMe.ts';
import type { SettingsSection } from '../registry.ts';
import { isPageStale } from '../pageStale.ts';

const PAGE_SHA = typeof __ORGUS_BUILD_SHA__ === 'string' ? __ORGUS_BUILD_SHA__ : '';

interface Health {
  version: string | null;
  /** The commit the hub process runs. */
  sha: string | null;
  /** The commit of the web bundle the hub started serving (null from a hub older than that stamp). */
  page: string | null;
  startedAt: number | null;
}

type HealthLoad = { phase: 'loading' } | { phase: 'ready'; health: Health } | { phase: 'error' };

/** GET /api/health: the hub's version, build and start time. Skipped on demo data. */
function useHealth(enabled: boolean, epoch = 0): HealthLoad {
  const [load, setLoad] = useState<HealthLoad>({ phase: 'loading' });
  useEffect(() => {
    if (!enabled) return;
    let stop = false;
    fetch('/api/health', { cache: 'no-store' })
      .then((r) => (r.ok ? (r.json() as Promise<{ version?: string; build?: { sha?: string | null; page?: string | null; startedAt?: number } }>) : Promise.reject(new Error(String(r.status)))))
      .then(
        (b) => !stop && setLoad({ phase: 'ready', health: { version: b.version ?? null, sha: b.build?.sha ?? null, page: b.build?.page ?? null, startedAt: b.build?.startedAt ?? null } }),
        () => !stop && setLoad({ phase: 'error' }),
      );
    return () => {
      stop = true;
    };
  }, [enabled, epoch]);
  return load;
}

const ROLE: Record<string, string> = { owner: 'Owner', admin: 'Admin', operator: 'Operator', viewer: 'Viewer' };

const SURFACE = 'rounded-card border border-border bg-surface surface-sheen shadow-elevation-flat';

/** One number of the hub's summary: a door to the page that lists it. */
/** `featured`: the strip's one brand figure, in the Prism headline gradient (colour.md, the warm role). */
function Stat({ to, label, value, meta, loading, text, featured }: { to?: string; label: string; value: ReactNode; meta?: ReactNode; loading?: boolean; text?: boolean; featured?: boolean }) {
  const body = (
    <>
      <span className="flex items-center gap-1 text-caption font-medium text-fg-muted">
        {label}
        {to && <ChevronRight className="size-3.5 text-fg-faint transition-transform duration-(--duration-fast) group-hover/stat:translate-x-0.5" aria-hidden />}
      </span>
      {loading ? (
        <Skeleton className="h-7 w-20" />
      ) : (
        // A name may wrap to a second line rather than lose its end; numbers stay on one line.
        <span className={cn('flex min-w-0 items-center font-semibold text-fg', text ? 'min-h-7 text-body' : 'h-7 truncate text-title num')}>
          <span className={cn(text ? 'line-clamp-2 break-words' : 'truncate', featured && 'prism-headline')}>{value}</span>
        </span>
      )}
      {meta != null && <span className="truncate text-caption text-fg-subtle">{meta}</span>}
    </>
  );
  const box = 'group/stat flex min-w-0 flex-col gap-1 rounded-control px-4 py-3';
  return to ? (
    <Link to={to} className={cn(box, 'transition-colors duration-(--duration-fast) hover:bg-hover focus-visible:bg-hover')}>
      {body}
    </Link>
  ) : (
    <div className={box}>{body}</div>
  );
}

/** This hub: who it is, where it runs, what code it runs, and what is connected to it. */
function HubSummary() {
  const { mode } = useConnection();
  const demo = mode === 'mock';
  const [epoch, setEpoch] = useState(0);
  const load = useHealth(!demo, epoch);
  const now = useNow(30_000);
  const machines = useOrgus((s) => s.machines);
  const projects = useOrgus((s) => s.projects);
  const sessions = useOrgus((s) => s.sessions);
  const me = useMe((s) => s.me);
  const online = Object.values(machines).filter((m) => m.online).length;
  const total = Object.keys(machines).length;
  const live = Object.values(sessions).filter((s) => s.status !== 'ended').length;
  const address = typeof window === 'undefined' ? '' : window.location.origin;
  const health = load.phase === 'ready' ? load.health : null;
  const stale = !!(health && PAGE_SHA && isPageStale(PAGE_SHA, health));
  const who = me ? (me.member?.name ?? me.member?.login ?? me.actor.id) : '';
  const role = me ? (ROLE[me.actor.role] ?? me.actor.role) : '';

  const copy = () => {
    void navigator.clipboard?.writeText(address).then(() => toast('Address copied'));
  };

  return (
    <section aria-label="This hub" className="flex flex-col gap-6">
      <div className="flex min-w-0 flex-wrap items-center gap-x-4 gap-y-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-card border border-border bg-surface shadow-elevation-flat">
          <OrgusMark size={28} decorative />
        </span>
        <div className="flex min-w-48 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-3">
            <span className="truncate text-lead font-semibold text-fg">This hub</span>
            <StatusPill status={demo ? 'idle' : load.phase === 'error' ? 'offline' : 'working'} label={demo ? 'Demo data' : load.phase === 'error' ? 'Not answering' : 'Running'} />
          </span>
          <span className="flex min-w-0 items-center gap-1">
            <code className="min-w-0 truncate font-mono text-caption text-fg-muted">{address}</code>
            <IconButton label="Copy address" icon={<Copy />} size="xs" onClick={copy} />
          </span>
        </div>
        <RestartHub onRestarted={() => setEpoch((e) => e + 1)} />
      </div>
      {stale && (
        <Banner tone="attention" icon={<TriangleAlert />} title="This page is newer than the hub">
          Restart the hub from here or with orgus restart, then reload.
        </Banner>
      )}
      <div className={cn(SURFACE, 'grid grid-cols-2 gap-1 p-1 md:grid-cols-4')}>
        <Stat to="/machines" label="Machines" value={`${formatInt(online)} of ${formatInt(total)}`} meta="online" />
        <Stat to="/projects" label="Projects" featured value={formatInt(Object.keys(projects).length)} meta={plural(live, 'live session')} />
        <Stat
          label="Version"
          loading={!demo && load.phase === 'loading'}
          value={demo ? 'Demo' : (health?.version ?? 'Unknown')}
          meta={demo ? 'No hub version' : health?.startedAt ? `Running ${formatRelative(health.startedAt, now).replace(/ ago$/, '')}${health.sha ? `, ${shortSha(health.sha)}` : ''}` : load.phase === 'error' ? 'The hub did not say' : undefined}
        />
        {me ? <Stat to="/settings/members" label="Signed in as" value={who} text meta={who.startsWith(role) ? undefined : role} /> : <Stat to="/settings/members" label="Sign-in" value="Open" text meta="No sign-in on this hub" />}
      </div>
    </section>
  );
}

const SHORTCUTS: { to: string; icon: ComponentType<{ className?: string }>; title: string; text: string }[] = [
  { to: '/settings/policies', icon: ScrollText, title: 'Approval rules', text: 'What agents may do without asking' },
  { to: '/settings/notifications', icon: Bell, title: 'Notifications', text: 'Where requests and failures reach you' },
  { to: '/settings/members', icon: Users, title: 'Members and sign-in', text: 'Who can sign in, and their role' },
  { to: '/settings/tokens', icon: KeyRound, title: 'API tokens', text: 'For scripts, CI and AI agents' },
  { to: '/settings/appearance', icon: Palette, title: 'Appearance', text: 'Theme and row density' },
  { to: '/getting-started', icon: Rocket, title: 'Getting started', text: 'A machine, a repo and your phone' },
];

function Shortcuts() {
  return (
    <section aria-labelledby="settings-most-used" className="flex flex-col gap-2">
      <h3 id="settings-most-used" className="px-1 text-ui font-medium text-fg">
        Most used
      </h3>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {SHORTCUTS.map((s) => (
          <li key={s.to} className="motion-arrive">
            <Link to={s.to} className={cn(SURFACE, 'group/short flex h-full items-start gap-3 px-4 py-3 transition-colors duration-(--duration-fast) hover:border-border-strong hover:bg-hover focus-visible:bg-hover')}>
              <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-active text-fg-muted group-hover/short:text-fg" aria-hidden>
                <s.icon className="size-4" />
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-ui font-medium text-fg">{s.title}</span>
                <span className="truncate text-caption text-fg-subtle">{s.text}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function General() {
  return (
    <div className="flex flex-col gap-8">
      <HubSummary />
      <Shortcuts />
    </div>
  );
}

const section: SettingsSection = {
  id: 'general',
  title: 'General',
  description: 'This hub and the settings people change most.',
  order: 0,
  group: 'workspace',
  keywords: ['hub', 'version', 'address', 'about', 'build'],
  icon: SlidersHorizontal,
  Component: General,
};
export default section;
